/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { RefObject, useEffect, useRef } from 'react';

export type LoadMoreSentinelProps = {
  rootRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onIntersect: () => void;
  rootMargin?: string;
  dataTestId?: string;
};

export function LoadMoreSentinel({
  rootRef,
  enabled,
  onIntersect,
  rootMargin = '150px',
  dataTestId = 'load-more-sentinel',
}: LoadMoreSentinelProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onIntersect);
  callbackRef.current = onIntersect;

  useEffect(() => {
    if (!enabled) return undefined;
    const root = rootRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callbackRef.current();
      },
      { root, rootMargin },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [rootRef, enabled, rootMargin]);

  return <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" data-testid={dataTestId} />;
}
